/**
 * License — the signed Eurion license of this server (docs/licensing-design.md).
 *
 * Talks to admin-service /v1/admin/license. Everyone with admin access sees the state; only
 * super admins install a license (paste or upload a .eurion-license file, preview first) or
 * check for a renewal. Enforcement is soft: an expired license never locks anyone out, it
 * only stops new users and add-on features (and updates past the support date).
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { KeyRound, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Upload, Loader2, Users, CalendarClock, Lock } from 'lucide-react';

type State = 'valid' | 'expiring' | 'grace' | 'restricted' | 'unlicensed' | 'not_yet_valid';
interface Status {
  state: State; reason: string; phaseEndsAt: string | null; daysLeft: number | null;
  activeUsers: number; maxActiveUsers: number; overLimit: boolean; canAddUsers: boolean;
  features: string[]; supportUntil: string | null;
  /** Per add-on (from the view): available, locked (bought, blocked now), hidden (not bought). */
  access?: FeatureAccess;
}
export interface FeatureAccess { available: string[]; locked: string[]; hidden: string[] }
interface License {
  license_id: string; serial: number; customer: { id: string; name: string; org_number?: string };
  org_name: string; domains: string[]; deployment: string; plan: string; max_active_users: number;
  features: string[]; valid_from: string; valid_until: string; grace_days: number; support_until: string;
  trial: boolean; installed_at?: string; source?: string; issued_at?: string;
}
interface View {
  status: Status; license: License | null; access?: FeatureAccess;
  online: { enabled: boolean; lastCheck: { at: string; ok: boolean; message: string } | null };
}

const BASE = '/v1/admin/license';
const FEATURE_LABEL: Record<string, string> = {
  ai: 'AI summaries and assistant', transcription: 'Transcription and captions', office: 'Office editing in the browser',
  mail: 'Eurion Mail', bridge: 'Microsoft 365 / chat bridge',
};
const ALL_FEATURES = Object.keys(FEATURE_LABEL);

export type FeatureRow = { feature: string; label: string; state: 'included' | 'not_included' | 'blocked' };
const ROW_TEXT: Record<FeatureRow['state'], string> = {
  included: 'Included', not_included: 'Not included', blocked: 'Blocked (licence expired)',
};

/**
 * Each add-on as the License page lists it: Included (usable now), Not included (not part
 * of the license: the apps hide it) or Blocked (bought, but the license is restricted: the
 * apps show it locked). Older admin-services without `access` fall back to status.features.
 */
export function featureRows(status: Pick<Status, 'state' | 'features'>, access?: FeatureAccess): FeatureRow[] {
  return ALL_FEATURES.map(feature => {
    let state: FeatureRow['state'];
    if (access) state = access.locked.includes(feature) ? 'blocked' : access.hidden.includes(feature) ? 'not_included' : 'included';
    else state = status.features.includes(feature) ? 'included' : status.state === 'restricted' ? 'blocked' : 'not_included';
    return { feature, label: FEATURE_LABEL[feature]!, state };
  });
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(BASE + path, init);
  const body = await res.json().catch(() => ({})) as { success?: boolean; data?: T; error?: { message?: string } };
  if (!res.ok || body.success === false) throw Object.assign(new Error(body.error?.message ?? `HTTP ${res.status}`), { status: res.status });
  return body.data as T;
}
const post = (path: string, license: string) => api<unknown>(path, {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ license }),
});

function day(iso?: string | null) { return iso ? new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso).toLocaleDateString() : '—'; }
function when(iso?: string | null) { return iso ? new Date(iso).toLocaleString() : '—'; }

/** What the banner at the top of the admin portal says, or null. */
export function bannerText(s: Status): { tone: 'warn' | 'bad'; text: string } | null {
  const d = s.daysLeft ?? 0;
  switch (s.state) {
    case 'expiring': return { tone: 'warn', text: `The Eurion license expires in ${d} day${d === 1 ? '' : 's'} (${day(s.phaseEndsAt)}). Contact Eurion to renew.` };
    case 'grace': return { tone: 'bad', text: `The Eurion license has expired. Everything still works for ${d} more day${d === 1 ? '' : 's'}; after that no new users or add-on features. Renew now.` };
    case 'restricted': return { tone: 'bad', text: s.reason === 'revoked'
      ? 'The Eurion license has been revoked. Existing users and data keep working; new users, add-on features and updates are stopped.'
      : 'No valid Eurion license. Existing users and data keep working; new users, add-on features and updates are stopped. Install a license.' };
    case 'unlicensed': return { tone: 'warn', text: `No license installed yet. Install your Eurion license within ${d} day${d === 1 ? '' : 's'}.` };
    default: return s.overLimit ? { tone: 'warn', text: `More active users (${s.activeUsers}) than the license allows (${s.maxActiveUsers}).${s.canAddUsers ? '' : ' New users can’t be added.'}` } : null;
  }
}

/** For App.tsx: license status (refreshed every 30 min) for the banner and sidebar dot. */
export function useLicenseStatus(enabled: boolean): Status | null {
  const [status, setStatus] = useState<Status | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = () => api<View>('').then(v => { if (alive) setStatus({ ...v.status, access: v.access }); }).catch(() => {});
    load();
    window.addEventListener('eurion-license-changed', load);
    const t = setInterval(load, 30 * 60 * 1000);
    return () => { alive = false; clearInterval(t); window.removeEventListener('eurion-license-changed', load); };
  }, [enabled]);
  return status;
}

function StateBadge({ state }: { state: State }) {
  const map: Record<State, [string, string]> = {
    valid: ['Valid', 'bg-nord-14/15 text-nord-14'], expiring: ['Expires soon', 'bg-nord-13/15 text-nord-13'],
    grace: ['Expired · grace period', 'bg-nord-12/15 text-nord-12'], restricted: ['Restricted', 'bg-nord-11/15 text-nord-11'],
    unlicensed: ['No license', 'bg-nord-13/15 text-nord-13'], not_yet_valid: ['Starts later', 'bg-nord-8/15 text-nord-8'],
  };
  const [label, cls] = map[state];
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}>{label}</span>;
}

export default function LicensePage({ isSuperAdmin }: { isSuperAdmin: boolean }) {
  const [view, setView] = useState<View | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [preview, setPreview] = useState<License | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try { setView(await api<View>('')); setError(null); } catch (e) { setError((e as Error).message); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const doPreview = async (t: string) => {
    setText(t); setPreview(null); setPreviewError(null); setNotice(null);
    if (!t.trim()) return;
    try { setPreview(await post('/preview', t) as License); } catch (e) { setPreviewError((e as Error).message); }
  };

  const install = async () => {
    setBusy('install'); setPreviewError(null);
    try {
      await post('', text);
      setText(''); setPreview(null); setNotice('License installed.');
      window.dispatchEvent(new Event('eurion-license-changed'));
      await load();
    } catch (e) { setPreviewError((e as Error).message); } finally { setBusy(null); }
  };

  const check = async () => {
    setBusy('check'); setNotice(null); setError(null);
    try {
      const r = await api<{ check: { ok: boolean; message: string } }>('/check', { method: 'POST' });
      setNotice(r.check.message || (r.check.ok ? 'Checked.' : 'Check failed.'));
      window.dispatchEvent(new Event('eurion-license-changed'));
      await load();
    } catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  };

  if (!view && !error) return <div className="p-8 text-sm text-nord-4"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading…</div>;

  const s = view?.status; const lic = view?.license;
  const banner = s ? bannerText(s) : null;
  const pct = s && s.maxActiveUsers > 0 ? Math.min(100, Math.round((s.activeUsers / s.maxActiveUsers) * 100)) : 0;

  return (
    <div className="max-w-3xl space-y-5 p-8">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-nord-6">License</h1>
          <p className="mt-1 text-sm text-nord-4">
            {lic ? <>{lic.org_name} · <span className="capitalize">{lic.plan}</span>{lic.trial ? ' (trial)' : ''} · <span className="font-mono">{lic.license_id}</span></> : 'No license installed'}
          </p>
        </div>
        {isSuperAdmin && view?.online.enabled && lic && (
          <button disabled={!!busy} onClick={() => void check()}
            className="flex items-center gap-2 rounded-lg border border-nord-3 px-3 py-1.5 text-sm text-nord-5 hover:bg-nord-2 disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${busy === 'check' ? 'animate-spin' : ''}`} /> Check for renewal
          </button>
        )}
      </div>

      {error && <div className="rounded-xl border border-nord-11/40 bg-nord-11/10 p-4 text-sm text-nord-5">{error}</div>}
      {notice && <div className="rounded-xl border border-nord-8/40 bg-nord-8/10 p-4 text-sm text-nord-5">{notice}</div>}
      {banner && (
        <div className={`flex items-start gap-3 rounded-xl border p-4 text-sm text-nord-5 ${banner.tone === 'bad' ? 'border-nord-11/40 bg-nord-11/10' : 'border-nord-13/40 bg-nord-13/10'}`}>
          <AlertTriangle className={`mt-0.5 h-4 w-4 flex-shrink-0 ${banner.tone === 'bad' ? 'text-nord-11' : 'text-nord-13'}`} /> {banner.text}
        </div>
      )}

      {s && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-nord-2 bg-nord-1 p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-nord-4"><KeyRound className="h-4 w-4" /> Status</div>
              <StateBadge state={s.state} />
            </div>
            <dl className="mt-4 space-y-1.5 text-sm">
              <div className="flex justify-between"><dt className="text-nord-4">Valid until</dt><dd className="text-nord-6">{lic ? day(lic.valid_until) : day(s.phaseEndsAt)}</dd></div>
              {lic && <div className="flex justify-between"><dt className="text-nord-4">Grace period</dt><dd className="text-nord-6">{lic.grace_days} days</dd></div>}
              <div className="flex justify-between"><dt className="text-nord-4">Updates & support until</dt><dd className="text-nord-6">{s.supportUntil ? day(s.supportUntil) : 'no limit'}</dd></div>
              {s.daysLeft !== null && s.state !== 'valid' && <div className="flex justify-between"><dt className="text-nord-4">Days left</dt><dd className="text-nord-6">{s.daysLeft}</dd></div>}
            </dl>
          </div>
          <div className="rounded-xl border border-nord-2 bg-nord-1 p-5">
            <div className="flex items-center gap-2 text-sm text-nord-4"><Users className="h-4 w-4" /> Active users</div>
            <div className="mt-3 text-2xl font-semibold text-nord-6">
              {s.activeUsers}<span className="text-base font-normal text-nord-4"> / {s.maxActiveUsers > 0 ? s.maxActiveUsers : '∞'}</span>
            </div>
            {s.maxActiveUsers > 0 && (
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-nord-2">
                <div className={`h-full ${s.overLimit ? 'bg-nord-11' : pct > 90 ? 'bg-nord-13' : 'bg-nord-8'}`} style={{ width: `${pct}%` }} />
              </div>
            )}
            <p className="mt-2 text-xs text-nord-3">Active accounts, not counting suspended or deleted ones. Suspend people who have left to free seats.</p>
          </div>
        </div>
      )}

      {s && (
        <div className="rounded-xl border border-nord-2 bg-nord-1 p-5">
          <div className="text-sm text-nord-4">Add-on features</div>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2" data-testid="license-features">
            {featureRows(s, view?.access).map(({ feature, label, state }) => (
              <li key={feature} className="flex items-center gap-2 text-sm" data-feature={feature} data-state={state}>
                {state === 'included' ? <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-nord-14" />
                  : state === 'blocked' ? <Lock className="h-4 w-4 flex-shrink-0 text-nord-11" />
                  : <XCircle className="h-4 w-4 flex-shrink-0 text-nord-3" />}
                <span className={state === 'included' ? 'text-nord-6' : 'text-nord-3'}>{label}</span>
                <span className={`ml-auto text-xs ${state === 'included' ? 'text-nord-14' : state === 'blocked' ? 'text-nord-11' : 'text-nord-3'}`}>{ROW_TEXT[state]}</span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-nord-3">Not included: hidden in the apps. Blocked: shown locked, with “Not included in your current licence, contact your administrator”.</p>
          <p className="mt-3 text-xs text-nord-3">Chat, meetings, files, calendar and data export are always available, whatever the license.</p>
        </div>
      )}

      {lic && (
        <div className="rounded-xl border border-nord-2 bg-nord-1 p-5 text-sm">
          <div className="text-nord-4">Details</div>
          <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5">
            <dt className="text-nord-4">Customer</dt><dd className="text-nord-6">{lic.customer.name}{lic.customer.org_number ? ` (${lic.customer.org_number})` : ''}</dd>
            <dt className="text-nord-4">Domains</dt><dd className="text-nord-6">{lic.domains.join(', ') || '—'}</dd>
            <dt className="text-nord-4">License</dt><dd className="font-mono text-nord-6">{lic.license_id} · version {lic.serial}</dd>
            <dt className="text-nord-4">Valid from</dt><dd className="text-nord-6">{day(lic.valid_from)}</dd>
            <dt className="text-nord-4">Installed</dt><dd className="text-nord-6">{when(lic.installed_at)}{lic.source === 'renewal' ? ' (online renewal)' : ''}</dd>
            <dt className="text-nord-4">Online renewal</dt>
            <dd className="text-nord-6">
              {view?.online.enabled ? (view.online.lastCheck
                ? <>last checked {when(view.online.lastCheck.at)}{view.online.lastCheck.ok ? '' : <span className="text-nord-12"> · {view.online.lastCheck.message}</span>}</>
                : 'on (not checked yet)') : 'off (offline server: paste renewed licenses below)'}
            </dd>
          </dl>
        </div>
      )}

      {isSuperAdmin ? (
        <div className="rounded-xl border border-dashed border-nord-3 p-5">
          <div className="flex items-center gap-3">
            <Upload className="h-5 w-5 text-nord-4" />
            <div className="flex-1">
              <div className="text-sm font-medium text-nord-5">{lic ? 'Install a new or renewed license' : 'Install your license'}</div>
              <div className="text-xs text-nord-4">Paste the license from Eurion's email, or choose the <span className="font-mono">.eurion-license</span> file. Only licenses signed by Eurion are accepted.</div>
            </div>
            <input ref={fileRef} type="file" accept=".eurion-license,.txt,.json" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void f.text().then(doPreview); e.target.value = ''; }} />
            <button onClick={() => fileRef.current?.click()} className="rounded-lg border border-nord-3 px-3 py-1.5 text-sm text-nord-5 hover:bg-nord-2">Choose file</button>
          </div>
          <textarea value={text} onChange={(e) => void doPreview(e.target.value)} rows={4} spellCheck={false}
            placeholder="-----BEGIN EURION LICENSE-----"
            className="mt-3 w-full rounded-lg border border-nord-3 bg-nord-0 px-3 py-2 font-mono text-xs text-nord-5 outline-none focus:border-nord-8" />
          {previewError && <p className="mt-2 text-sm text-nord-11">{previewError}</p>}
          {preview && (
            <div className="mt-3 rounded-lg border border-nord-8/40 bg-nord-0 p-3 text-sm text-nord-5">
              <div className="flex items-center gap-2 font-semibold text-nord-6"><CalendarClock className="h-4 w-4 text-nord-8" /> {preview.org_name} · <span className="capitalize">{preview.plan}</span>{preview.trial ? ' (trial)' : ''}</div>
              <div className="mt-1 text-xs text-nord-4">
                {preview.max_active_users > 0 ? `${preview.max_active_users} users` : 'unlimited users'} · {day(preview.valid_from)} – {day(preview.valid_until)}
                {' '}· {preview.features.length ? preview.features.map(f => FEATURE_LABEL[f] ?? f).join(', ') : 'no add-on features'}
              </div>
              <button disabled={!!busy} onClick={() => void install()}
                className="mt-3 rounded-lg bg-nord-8 px-4 py-2 text-sm font-semibold text-nord-0 hover:bg-nord-7 disabled:opacity-50">
                {busy === 'install' ? 'Installing…' : 'Install license'}
              </button>
            </div>
          )}
        </div>
      ) : <p className="text-xs text-nord-3">Only super administrators can install a license.</p>}
    </div>
  );
}
